/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Command Palette — Premium full-screen search for Stockpilot
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Architecture decisions:
 *
 * 1. ALWAYS in the DOM — the palette is never mounted/unmounted. Visibility is
 *    controlled with CSS `opacity` + `pointer-events` + `visibility`.
 * 2. `position: fixed; z-index: 300` — own compositor layer, independent from
 *    AppBackground's blur layers.
 * 3. CSS-only transitions.
 * 4. Keyboard-first — arrow navigation, Enter to select, Escape to close.
 * 5. Recent searches — persisted in localStorage, shown when input is empty.
 *
 * The primary search subject is the inventory primary entity: `product`.
 */

import { msg } from "@lingui/core/macro";
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  Match,
  on,
  onCleanup,
  onMount,
  Show,
  Switch,
} from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { ArrowRight, Boxes, Clock, Package, Search, ShoppingCart, X, Zap } from "lucide-solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { and, product, sql } from "@stockpilot/powersync";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { useNavigate } from "@/lib/u-router";
import { cn, liveProductFilters, queryKeys } from "@stockpilot/shared";
import { useI18n } from "@/providers/i18n-provider";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useCommandHandler, useScope } from "@/lib/shortcuts/solid";
import { useBackDismiss } from "@/lib/use-back-dismiss";
import { loadRecent, removeRecentTerm, saveRecent } from "./command-palette-recent";
import { ResultItem } from "./command-palette-result-item";
import type { QuickAction, SearchResult } from "./command-palette.types";

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const EXIT_ANIMATION_MS = 200;
const FOCUS_MID_TRANSITION_MS = 50;

// ─── CommandPalette ───────────────────────────────────────────────────────────

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette(props: CommandPaletteProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [query, setQuery] = createSignal("");
  const [debouncedQuery, setDebouncedQuery] = createSignal("");
  const [activeIndex, setActiveIndex] = createSignal(0);
  const [recent, setRecent] = createSignal<string[]>(loadRecent());

  // ── Refs ───────────────────────────────────────────────────────────────────
  let inputRef: HTMLInputElement | undefined = undefined;
  let scrollRef: HTMLDivElement | undefined = undefined;

  // ── Basic handlers ─────────────────────────────────────────────────────────
  const close = () => {
    props.onClose();
  };

  // Browser back closes the palette instead of navigating away (PWA pattern).
  useBackDismiss(
    () => props.isOpen,
    () => close(),
  );

  const handleSelectResult = (item: SearchResult) => {
    // Persist the product name as a recent search, then deep-link to the picked
    // product. PATTERN A: the chosen option must navigate to ITS OWN copyable URL
    // (`/products?edit=<id>`), not to the generic catalog — so the destination is
    // reproducible when pasted/reloaded. `close()` here is a pure signal flip (the
    // palette open-state is NOT a URL param), so it never overwrites the navigate.
    const term = query().trim() || (item.text ?? "");
    if (term) {
      setRecent(saveRecent(term, recent()));
    }
    void navigate({ to: "/products", search: { edit: item.id } });
    close();
  };

  const handleRecentClick = (term: string) => {
    setQuery(term);
    inputRef?.focus();
  };

  const removeRecent = (e: MouseEvent, term: string) => {
    e.stopPropagation();
    setRecent((prev) => removeRecentTerm(term, prev));
  };

  // ── Debounce ───────────────────────────────────────────────────────────────
  createEffect(() => {
    const q = query();
    const timer = setTimeout(() => setDebouncedQuery(q), DEBOUNCE_MS);
    onCleanup(() => {
      clearTimeout(timer);
    });
  });

  // Reset index when query changes
  createEffect(
    on(debouncedQuery, () => {
      setActiveIndex(0);
    }),
  );

  // Focus input when palette opens
  createEffect(
    on(
      () => props.isOpen,
      (open) => {
        if (open) {
          requestAnimationFrame(() => {
            inputRef?.focus();
            inputRef?.select();
          });

          const t1 = setTimeout(() => {
            inputRef?.focus();
            inputRef?.select();
          }, FOCUS_MID_TRANSITION_MS);

          onCleanup(() => {
            clearTimeout(t1);
          });
        } else {
          const t3 = setTimeout(() => {
            setQuery("");
            setActiveIndex(0);
          }, EXIT_ANIMATION_MS);

          onCleanup(() => {
            clearTimeout(t3);
          });
        }
      },
    ),
  );

  // ── Search query ───────────────────────────────────────────────────────────
  const searchResults = createPowerSyncQuery<SearchResult>({
    queryKey: () => queryKeys.search.results(debouncedQuery()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const q = debouncedQuery().trim();
      if (!dbInstance || q.length < MIN_QUERY_LENGTH) {
        return null;
      }

      const escapedQ = `%${q}%`;
      const limit = 30;

      // SOFT_DELETE_STANDARD — bug de invariante disperso corregido: esta
      // búsqueda era CRUDA (sin `deleted_at`), así que ⌘K mostraba productos
      // en la papelera en los resultados. Ahora compone `liveProductFilters()`,
      // igual que el resto de vistas del catálogo.
      return dbInstance
        .select({
          type: sql<"product">`'product'`.as("type"),
          id: product.id,
          text: product.name,
          sku: product.sku,
        })
        .from(product)
        .where(
          and(
            sql`${product.name} LIKE ${escapedQ} OR ${product.sku} LIKE ${escapedQ}`,
            ...liveProductFilters(),
          ),
        )
        .limit(limit);
    },
    enabled: () => debouncedQuery().length >= MIN_QUERY_LENGTH && Boolean(drizzle()),
    placeholderData: keepPreviousData,
    suspense: false,
  });

  // ── Quick actions ──────────────────────────────────────────────────────────
  const quickActions = createMemo<QuickAction[]>(() => [
    {
      id: "goto-products",
      label: t(msg`Go to Products`),
      icon: () => <Package class="h-4 w-4" />,
      action: () => {
        void navigate({ to: "/products" });
        close();
      },
    },
    {
      id: "goto-stock",
      label: t(msg`Go to Stock`),
      icon: () => <Boxes class="h-4 w-4" />,
      action: () => {
        void navigate({ to: "/stock" });
        close();
      },
    },
    {
      id: "goto-purchases",
      label: t(msg`Go to Purchases`),
      icon: () => <ShoppingCart class="h-4 w-4" />,
      action: () => {
        void navigate({ to: "/purchases" });
        close();
      },
    },
  ]);

  // ── Flat navigable items ───────────────────────────────────────────────────
  type NavItem =
    | { kind: "result"; item: SearchResult }
    | { kind: "recent"; term: string }
    | { kind: "action"; action: QuickAction };

  const navItems = createMemo<NavItem[]>(() => {
    const q = query().trim();
    const results = searchResults.data ?? [];

    if (q.length >= MIN_QUERY_LENGTH) {
      return results.map((item): NavItem => ({ kind: "result", item }));
    }

    const recentItems = recent().map((term): NavItem => ({ kind: "recent", term }));
    const actionItems = quickActions().map((action): NavItem => ({ kind: "action", action }));
    return [...recentItems, ...actionItems];
  });

  // ── Navigation helpers ─────────────────────────────────────────────────────
  const clampIndex = (i: number) => Math.max(0, Math.min(i, navItems().length - 1));

  const scrollActiveIntoView = () => {
    if (!scrollRef) {
      return;
    }
    const active = scrollRef.querySelector<HTMLElement>("[data-active='true']");
    active?.scrollIntoView({ block: "nearest" });
  };

  const selectActive = () => {
    const items = navItems();
    const idx = activeIndex();
    const item = items[idx];
    if (!item) {
      return;
    }

    if (item.kind === "result") {
      handleSelectResult(item.item);
    } else if (item.kind === "recent") {
      setQuery(item.term);
    } else {
      item.action.action();
    }
  };

  // ── Keyboard handling ──────────────────────────────────────────────────────
  useScope("palette", () => props.isOpen);
  useCommandHandler(COMMAND_IDS.PALETTE_CLOSE, () => {
    if (!props.isOpen) {
      return;
    }
    close();
  });
  useCommandHandler(COMMAND_IDS.PALETTE_NEXT, () => {
    if (!props.isOpen) {
      return;
    }
    setActiveIndex((i) => clampIndex(i + 1));
    requestAnimationFrame(scrollActiveIntoView);
  });
  useCommandHandler(COMMAND_IDS.PALETTE_PREV, () => {
    if (!props.isOpen) {
      return;
    }
    setActiveIndex((i) => clampIndex(i - 1));
    requestAnimationFrame(scrollActiveIntoView);
  });
  useCommandHandler(COMMAND_IDS.PALETTE_CONFIRM, () => {
    if (!props.isOpen) {
      return;
    }
    selectActive();
  });

  // Keep input focused while the command palette is open
  onMount(() => {
    const handleFocusOut = () => {
      setTimeout(() => {
        if (!props.isOpen || !inputRef) {
          return;
        }

        const related = document.activeElement;
        const panel = inputRef.closest(".command-palette-panel");
        if (!panel) {
          return;
        }

        if (!related || related === document.body || !panel.contains(related)) {
          inputRef.focus();
        }
      }, 0);
    };

    inputRef?.addEventListener("focusout", handleFocusOut);
    onCleanup(() => {
      inputRef?.removeEventListener("focusout", handleFocusOut);
    });
  });

  // ── Derived UI state ───────────────────────────────────────────────────────
  const hasQuery = () => query().trim().length >= MIN_QUERY_LENGTH;
  const isSearching = () => {
    if (!hasQuery()) {
      return false;
    }
    if (!searchResults.data) {
      return query().trim() !== debouncedQuery().trim() || searchResults.isFetching;
    }
    return searchResults.isFetching && !searchResults.data;
  };
  const results = () => searchResults.data ?? [];
  const hasResults = () => results().length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      class="fixed inset-0 z-[300] flex items-start justify-center pt-[clamp(40px,8vh,120px)] px-4 pb-6 bg-background/40 backdrop-blur-[12px] backdrop-saturate-[1.4] transition-all duration-180 ease-elegant will-change-[opacity,visibility]"
      style={{
        opacity: props.isOpen ? "1" : "0",
        "pointer-events": props.isOpen ? "auto" : "none",
        visibility: props.isOpen ? "visible" : "hidden",
      }}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) {
          close();
        }
      }}
    >
      <div
        class="command-palette-panel w-full max-w-[620px] max-h-[min(80vh,700px)] flex flex-col bg-card/92 border border-border/70 rounded-2xl shadow-[0_0_0_1px_color-mix(in_oklch,var(--primary)_8%,transparent),0_8px_32px_-4px_rgba(0,0,0,0.3),0_32px_64px_-8px_rgba(0,0,0,0.2)] overflow-hidden transition-all duration-180 ease-snappy will-change-transform [backface-visibility:hidden]"
        style={{
          transform: props.isOpen
            ? "scale3d(1, 1, 1) translate3d(0, 0, 0)"
            : "scale3d(0.97, 0.97, 1) translate3d(0, -8px, 0)",
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t(msg`Search`)}
      >
        {/* ── Header / Input ──────────────────────────────────────────────── */}
        <div class="flex items-center gap-2.5 py-3.5 px-4 border-b border-border/50 shrink-0">
          <div class="text-muted-foreground flex items-center shrink-0">
            <Search class="h-4.5 w-4.5" />
          </div>

          <input
            ref={setInputRef}
            type="text"
            class="flex-1 bg-transparent border-none outline-none text-[15px] font-normal text-foreground caret-primary min-w-0 tracking-[-0.01em] placeholder:text-muted-foreground/70"
            placeholder={t(msg`Search products or type a command...`)}
            value={query()}
            onInput={(e) => setQuery(e.currentTarget.value)}
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck={false}
          />

          <button
            type="button"
            class="flex items-center justify-center w-5.5 h-5.5 rounded-md text-muted-foreground bg-muted/60 border-none cursor-pointer shrink-0 transition-colors duration-120 ease-out hover:bg-muted hover:text-foreground"
            style={{
              display: query() ? "flex" : "none",
            }}
            onClick={() => {
              setQuery("");
              inputRef?.focus();
            }}
            aria-label={t(msg`Clear search`)}
          >
            <X class="h-3.5 w-3.5" />
          </button>

          <button
            type="button"
            class="flex items-center bg-transparent border-none cursor-pointer shrink-0 p-0"
            onClick={close}
            aria-label={t(msg`Close`)}
          >
            <kbd class="inline-flex items-center py-0.5 px-1.5 rounded-[5px] border border-border/60 bg-muted/50 font-mono text-[10px] font-semibold text-muted-foreground tracking-[0.02em]">
              esc
            </kbd>
          </button>
        </div>

        <div
          ref={setScrollRef}
          class="flex-1 overflow-y-auto p-1.5 scroll-smooth overscroll-contain [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border/60 [&::-webkit-scrollbar-thumb]:rounded-full"
        >
          {/* ── Empty state: recents + quick actions ───────────────────── */}
          <div style={{ display: hasQuery() ? "none" : undefined }}>
            <Show when={recent().length > 0}>
              <div class="flex items-center gap-1.5 pt-2 pb-1 px-2.5 text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground/60">
                <Clock class="h-3 w-3" />
                {t(msg`Recent searches`)}
              </div>
              <For each={recent()}>
                {(term, i) => (
                  <div class="relative">
                    <button
                      type="button"
                      data-active={activeIndex() === i()}
                      class={cn(
                        "peer flex items-center gap-2.5 w-full py-[9px] px-2.5 rounded-[10px] border-none bg-transparent cursor-pointer text-left transition-colors duration-80 ease-out text-foreground hover:bg-[color-mix(in_oklch,var(--accent)_70%,transparent)]",
                        activeIndex() === i() &&
                          "bg-[color-mix(in_oklch,var(--primary)_10%,var(--accent)_60%)] active hover:bg-[color-mix(in_oklch,var(--primary)_10%,var(--accent)_60%)]",
                      )}
                      onClick={() => {
                        handleRecentClick(term);
                      }}
                      onPointerEnter={() => setActiveIndex(i())}
                    >
                      <span class="flex items-center justify-center w-7 h-7 rounded-lg shrink-0 bg-[color-mix(in_oklch,var(--muted)_50%,transparent)] text-[color-mix(in_oklch,var(--muted-foreground)_60%,transparent)] transition-colors duration-80 ease-out">
                        <Clock class="h-3.5 w-3.5" />
                      </span>
                      <span
                        class="flex-1 text-[13.5px] font-[450] truncate tracking-[-0.01em]"
                        style={{ "padding-right": "24px" }}
                      >
                        {term}
                      </span>
                    </button>
                    <button
                      type="button"
                      class="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-4.5 h-4.5 rounded border-none bg-transparent text-muted-foreground cursor-pointer opacity-0 shrink-0 transition-all duration-80 ease-out peer-hover:opacity-60 peer-[.active]:opacity-60 hover:opacity-100! hover:bg-destructive/12 hover:text-destructive"
                      onClick={(e) => {
                        removeRecent(e, term);
                      }}
                      aria-label={t(msg`Remove from recent`)}
                    >
                      <X class="h-3 w-3" />
                    </button>
                  </div>
                )}
              </For>
            </Show>

            <div
              class="flex items-center gap-1.5 pt-2 pb-1 px-2.5 text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground/60"
              style={{ "margin-top": recent().length > 0 ? "4px" : "0" }}
            >
              <Zap class="h-3 w-3" />
              {t(msg`Quick navigation`)}
            </div>
            <For each={quickActions()}>
              {(action, i) => {
                const gi = () => recent().length + i();
                return (
                  <button
                    type="button"
                    data-active={activeIndex() === gi()}
                    class={cn(
                      "flex items-center gap-2.5 w-full py-[9px] px-2.5 rounded-[10px] border-none bg-transparent cursor-pointer text-left transition-colors duration-80 ease-out text-foreground hover:bg-[color-mix(in_oklch,var(--accent)_70%,transparent)]",
                      activeIndex() === gi() &&
                        "bg-[color-mix(in_oklch,var(--primary)_10%,var(--accent)_60%)] active hover:bg-[color-mix(in_oklch,var(--primary)_10%,var(--accent)_60%)]",
                    )}
                    onClick={() => {
                      action.action();
                    }}
                    onPointerEnter={() => setActiveIndex(gi())}
                  >
                    <span class="flex items-center justify-center w-7 h-7 rounded-lg shrink-0 bg-[color-mix(in_oklch,var(--primary)_12%,transparent)] text-[var(--primary)] transition-colors duration-80 ease-out">
                      {action.icon()}
                    </span>
                    <span class="flex-1 text-[13.5px] font-[450] truncate tracking-[-0.01em]">
                      {action.label}
                    </span>
                    <ArrowRight class="h-3.5 w-3.5 opacity-30 ml-auto shrink-0" />
                  </button>
                );
              }}
            </For>
          </div>

          {/* ── Search results ─────────────────────────────────────────── */}
          <div style={{ display: hasQuery() ? undefined : "none" }}>
            <Switch>
              <Match when={isSearching()}>
                <div class="flex flex-col items-center justify-center gap-2 py-10 px-6 text-muted-foreground">
                  <div class="w-5 h-5 rounded-full border-2 border-primary/20 border-t-primary animate-[spin_0.6s_linear_infinite] will-change-transform" />
                  <span>{t(msg`Searching...`)}</span>
                </div>
              </Match>

              <Match when>
                <Show
                  when={hasResults()}
                  fallback={
                    <div class="flex flex-col items-center justify-center gap-2 py-10 px-6 text-muted-foreground">
                      <Search class="h-8 w-8 opacity-20" />
                      <p class="text-sm font-medium">{t(msg`No results found`)}</p>
                      <p class="text-xs opacity-50">
                        {(() => {
                          const term = query();
                          return t(msg`Try searching for "${term}"`);
                        })()}
                      </p>
                    </div>
                  }
                >
                  <For each={results()}>
                    {(item, idx) => (
                      <ResultItem
                        item={item}
                        index={idx()}
                        activeIndex={activeIndex()}
                        globalIndex={navItems().findIndex(
                          (n) => n.kind === "result" && n.item.id === item.id,
                        )}
                        onSelect={() => {
                          handleSelectResult(item);
                        }}
                        onHover={(gi) => setActiveIndex(gi)}
                      />
                    )}
                  </For>
                </Show>
              </Match>
            </Switch>
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div class="flex items-center gap-4 py-2 px-4 border-t border-border/40 bg-muted/20 shrink-0 flex-wrap">
          <span class="flex items-center gap-1.25 text-[10px] text-muted-foreground/55 whitespace-nowrap">
            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/50 font-mono text-[9px] font-bold text-muted-foreground">
              ↑↓
            </kbd>{" "}
            {t(msg`navigate`)}
          </span>
          <span class="flex items-center gap-1.25 text-[10px] text-muted-foreground/55 whitespace-nowrap">
            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/50 font-mono text-[9px] font-bold text-muted-foreground">
              ↵
            </kbd>{" "}
            {t(msg`select`)}
          </span>
          <span class="flex items-center gap-1.25 text-[10px] text-muted-foreground/55 whitespace-nowrap">
            <kbd class="inline-flex items-center py-0.25 px-1.25 rounded border border-border/50 bg-muted/50 font-mono text-[9px] font-bold text-muted-foreground">
              Esc
            </kbd>{" "}
            {t(msg`close`)}
          </span>
        </div>
      </div>
    </div>
  );

  function setInputRef(el: HTMLInputElement) {
    inputRef = el;
  }
  function setScrollRef(el: HTMLDivElement) {
    scrollRef = el;
  }
}
